import { useAuthContext } from "@/context/AuthContext";
import { useContentSelectieContext } from "@/context/ContentSelectieContext";
import {
  formatTheorieTitel,
  formatTitel,
  getVraagDisplayCode
} from "@/lib/utils";
import { useMemo } from "react";

export default function ModusSelectSessionChips() {
  const { userData } = useAuthContext();
  const { currentHoofdstuk } = useContentSelectieContext();

  const savedSession = userData?.huiswerk_set;

  // Build lookup: vraagId → { code, displayCode, subs, sectieTitel }
  const vraagLookup = useMemo(() => {
    if (!currentHoofdstuk?.secties)
      return new Map<
        number,
        {
          code: string;
          displayCode: string;
          subs: Map<number, string>;
          sectieTitel: string;
        }
      >();
    const map = new Map<
      number,
      {
        code: string;
        displayCode: string;
        subs: Map<number, string>;
        sectieTitel: string;
      }
    >();
    for (const sectie of currentHoofdstuk.secties) {
      for (const vraag of sectie.vragen) {
        const subs = new Map<number, string>();
        for (const sv of vraag.subvragen ?? []) {
          subs.set(sv.id, sv.letter);
        }
        map.set(vraag.id, {
          code: vraag.code,
          displayCode: getVraagDisplayCode(vraag, sectie),
          subs,
          sectieTitel: formatTitel(sectie.titel)
        });
      }
    }
    return map;
  }, [currentHoofdstuk]);

  // Build chips for the saved session per groep
  const sessionChips = useMemo(() => {
    if (!savedSession || vraagLookup.size === 0) return null;
    if (savedSession.groepen.length === 0) return null;
    return savedSession.groepen.map((groep) => {
      const chips: { label: string; subvraagId: number | null }[] = [];
      for (const v of groep.vragen) {
        const info = vraagLookup.get(v.id);
        const code = info?.displayCode ?? v.code;
        if (v.subvragen.length <= 1) {
          const onlySid = v.subvragen[0]?.id ?? null;
          chips.push({ label: code, subvraagId: onlySid });
        } else if (info) {
          const selected = savedSession.subvraagIds.filter((sid) =>
            info.subs.has(sid)
          );
          if (selected.length === 0) {
            chips.push({ label: code, subvraagId: null });
          } else {
            for (const sid of selected) {
              const letter = info.subs.get(sid);
              chips.push({ label: `${code}${letter}`, subvraagId: sid });
            }
          }
        } else {
          chips.push({ label: code, subvraagId: null });
        }
      }
      // Exercise-only groups (titel="") use sectie title from their first vraag
      let titel: string;
      if (groep.titel === "") {
        const firstInfo = vraagLookup.get(groep.vragen[0]?.id);
        titel = firstInfo?.sectieTitel ?? "Opgaven";
      } else {
        titel = formatTheorieTitel(groep.titel);
      }
      return { titel, chips };
    });
  }, [savedSession, vraagLookup]);

  if (!savedSession) return;

  return (
    <div className="px-3.5 py-3">
      {sessionChips ? (
        <div className="flex flex-col gap-2.5">
          {sessionChips.map((group, i) => (
            <div key={i}>
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-[10px] font-semibold text-huiswerk/50 uppercase tracking-wider">
                  {group.titel}
                </span>
              </div>
              <div className="flex flex-wrap gap-1">
                {group.chips.map((chip, j) => {
                  const done =
                    chip.subvraagId != null &&
                    savedSession.completedSubvraagIds.includes(chip.subvraagId);
                  const skipped =
                    chip.subvraagId != null &&
                    savedSession.skippedSubvraagIds.includes(chip.subvraagId);
                  return (
                    <span
                      key={j}
                      className={`inline-flex items-center justify-center min-w-[26px] h-[22px] px-1.5 rounded-md text-[11px] font-semibold tabular-nums bg-white ${
                        done
                          ? "border-[1.5px] border-green-500 text-green-700"
                          : skipped
                            ? "border-[1.5px] border-dashed border-green-400 text-gray-600"
                            : "border-[1.5px] border-gray-300 text-gray-600"
                      }`}
                    >
                      {chip.label}
                    </span>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <span className="text-xs text-gray-400">
          {savedSession.vraagIds.length} opgave
          {savedSession.vraagIds.length !== 1 ? "n" : ""}
        </span>
      )}
    </div>
  );
}
